import { BarChart, PlusCircle, ShoppingBasket, CreditCard, Shield, AlertTriangle, CheckCircle, Lock, Image, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";

import AnalyticsTab from "../components/AnalyticsTab";
import CreateProductForm from "../components/CreateProductForm";
import ProductsList from "../components/ProductsList";
import POSSystem from "../components/POSSystem";
import POSSecurityEnhanced from "../components/POSSecurityEnhanced";
import TwoFactorAuth from "../components/TwoFactorAuth";
import ImageImportManager from "../components/ImageImportManager";
import AuthManagementSystem from "../components/AuthManagementSystem";
import DeveloperSecurityGate from "../components/DeveloperSecurityGate";
import { useProductStore } from "../stores/useProductStore";
import { useUserStore } from "../stores/useUserStore";

const AdminPage = () => {
	const [activeTab, setActiveTab] = useState("pos-secure"); // 🔐 POS מאובטח כברירת מחדל
	const { fetchAllProducts } = useProductStore();
	const { user } = useUserStore();

	const tabs = [
		{ id: "pos-secure", label: "🔐 קופה מאובטחת", icon: CreditCard },
		{ id: "pos", label: "קופה רגילה", icon: CreditCard },
		{ id: "create", label: "יצירת מוצר", icon: PlusCircle },
		{ id: "products", label: "מוצרים", icon: ShoppingBasket },
		{ id: "analytics", label: "ניתוח נתונים", icon: BarChart },
		{ id: "2fa", label: "🛡️ 2FA", icon: Shield },
		{ id: "image-import", label: "🖼️ ייבוא תמונות", icon: Image },
		{ id: "auth-management", label: "👥 ניהול משתמשים", icon: Users },
	];

	useEffect(() => {
		fetchAllProducts();
	}, [fetchAllProducts]);

	return (
		    <div className="min-h-screen bg-white mt-20">
			<div className="container mx-auto px-4 py-8">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					className="text-center mb-8"
				>
					<h1 className="text-4xl font-bold text-gray-900 mb-2">
						🛡️ מערכת ניהול מאובטחת
					</h1>
					<p className="text-gray-600">
						פאנל מנהל מתקדם עם אבטחה מושלמת - SHA-SHA
					</p>
				</motion.div>

				{/* 🔐 אזהרת אבטחה */}
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.6 }}
					className="bg-gray-50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-gray-200"
				>
					<div className="flex items-center space-x-3 mb-4">
						<Shield className="text-gray-600 w-6 h-6" />
						<h2 className="text-xl font-bold text-gray-800">מערכת אבטחה מתקדמת - SHA-SHA</h2>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
						<div className="bg-red-50 rounded-xl p-4 border border-red-200">
							<div className="flex items-center space-x-2 mb-2">
								<AlertTriangle className="text-yellow-600 w-5 h-5" />
								<span className="text-gray-800 font-medium">2FA זמין</span>
							</div>
							<p className="text-red-700 text-sm">
								אימות דו-שלבי למנהלים
							</p>
						</div>
						<div className="bg-green-50 rounded-xl p-4 border border-green-200">
							<div className="flex items-center space-x-2 mb-2">
								<CheckCircle className="text-green-600 w-5 h-5" />
								<span className="text-green-800 font-medium">לוגים מאובטחים</span>
							</div>
							<p className="text-green-700 text-sm">
								מידע רגיש מוצפן
							</p>
						</div>
						<div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
							<div className="flex items-center space-x-2 mb-2">
								<Lock className="text-blue-600 w-5 h-5" />
								<span className="text-blue-800 font-medium">POS מאובטח</span>
							</div>
							<p className="text-blue-700 text-sm">
								מערכת קופה מתקדמת
							</p>
						</div>
						<div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
							<div className="flex items-center space-x-2 mb-2">
								<Shield className="text-purple-600 w-5 h-5" />
								<span className="text-purple-800 font-medium">מערכות מפתח</span>
							</div>
							<p className="text-purple-700 text-sm">
								גישה מוגבלת למפתחים
							</p>
						</div>
					</div>
				</motion.div>

				{/* Navigation Tabs */}
				<div className="flex flex-wrap justify-center gap-4 mb-8">
					{tabs.map((tab) => (
						<motion.button
							key={tab.id}
							onClick={() => setActiveTab(tab.id)}
							className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
								activeTab === tab.id
									? "bg-blue-600 text-white shadow-lg shadow-blue-500/25"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200"
							}`}
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<tab.icon className="w-5 h-5" />
							{tab.label}
						</motion.button>
					))}
				</div>

				{/* Tab Content */}
				<motion.div
					key={activeTab}
					initial={{ opacity: 0, x: 20 }}
					animate={{ opacity: 1, x: 0 }}
					transition={{ duration: 0.5 }}
					className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xl"
				>
					{activeTab === "pos-secure" && <POSSecurityEnhanced />}
					{activeTab === "pos" && <POSSystem />}
					{activeTab === "create" && <CreateProductForm />}
					{activeTab === "products" && <ProductsList />}
					{activeTab === "analytics" && <AnalyticsTab />}
					{activeTab === "2fa" && <TwoFactorAuth user={user} />}
					{activeTab === "image-import" && (
						<DeveloperSecurityGate requiredLevel="developer">
							<ImageImportManager />
						</DeveloperSecurityGate>
					)}
					{activeTab === "auth-management" && (
						<DeveloperSecurityGate requiredLevel="developer">
							<AuthManagementSystem />
						</DeveloperSecurityGate>
					)}
				</motion.div>
			</div>
		</div>
	);
};
export default AdminPage;
